import { useMutation } from '@apollo/client'
import { useTheme } from '@emotion/react'
import {
    Button,
    FormControlLabel,
    FormHelperText,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material'
import {
    type GetFilteredRostersWithCandidateQuery,
    JobBoardNotInterestedReasons,
} from '__generated__/graphql'
import { JobBoardFormValues } from 'candidate/pages/JobBoard'
import InterestedFormFields from 'candidate/pages/JobBoard/InterestedFormFields'
import NotInterestedFormFields from 'candidate/pages/JobBoard/NotInterestedFormFields'
import { useFormik } from 'formik'
import { FC, useEffect } from 'react'
import {
    GetFilteredRostersWithCandidate,
    GetRosterSearchV2,
} from 'recruiter/contexts'
import { UpdateJobBoardResponse } from 'recruiter/graphql/candidateProfile.gql'
import useToast from 'shared/components/Toast/useToast'
import UpdatedText from 'shared/components/UpdatedText/UpdatedText'
import { getFullName, getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'

interface RosterPanelJobBoardProps {
    jobBoardData: GetFilteredRostersWithCandidateQuery['rosterSearch']['rosters'][number]['rosterCandidate']['mostRecentJobBoardResponse']
    canEdit?: boolean
}

const validationSchema: Yup.ObjectSchema<JobBoardFormValues> = Yup.object({
    interested: Yup.boolean().nullable(),
    interestedComments: Yup.string().optional(),
    notInterestedComments: Yup.string().optional(),
    sectorIds: Yup.array()
        .required()
        .of(Yup.string().required())
        .when('interested', {
            is: true,
            then: (schema) => schema.min(1, 'Sector preferences are required'),
        }),
    locationIds: Yup.array()
        .required()
        .of(Yup.string().required())
        .when('interested', {
            is: true,
            then: (schema) =>
                schema.min(1, 'Location preferences are required'),
        }),

    notInterestedReason: Yup.mixed<JobBoardNotInterestedReasons>()
        .required('Please select a reason')
        .oneOf(Object.values(JobBoardNotInterestedReasons))
        .when('interested', {
            is: true,
            then: (schema) => schema.notRequired(),
        }),
    disabled: Yup.boolean().required(),
})

const RosterPanelJobBoard: FC<RosterPanelJobBoardProps> = ({
    jobBoardData,
    canEdit,
}) => {
    const theme = useTheme()
    const toast = useToast()
    const [updateJobBoardResponse] = useMutation(UpdateJobBoardResponse)
    const jobBoardId = jobBoardData?.id
    const jobBoardName = jobBoardData?.jobBoard?.name
    const jobBoardDescription = jobBoardData?.jobBoard?.description
    const companyName = jobBoardData?.jobBoard?.company?.value
    const sectors = jobBoardData?.jobBoard?.sectors?.map((s) => (
        <Typography
            color={theme.palette.other.secondary_light}
            variant="body2"
            key={s.sector?.id}
        >
            {s.sector?.value}
        </Typography>
    ))
    const startDate = jobBoardData?.jobBoard?.startDate
    const locations = jobBoardData?.jobBoard?.companyOffices?.map((co) => (
        <Typography
            key={co.companyOffice?.id}
            color={theme.palette.other.secondary_light}
            variant="body2"
        >
            {co.companyOffice?.officeLocation?.value}
        </Typography>
    ))

    const titles = jobBoardData?.jobBoard?.titles?.map((t) => (
        <Typography
            color={theme.palette.other.secondary_light}
            variant="body2"
            key={t.title?.id}
        >
            {t.title?.value}
        </Typography>
    ))

    const initialValues: JobBoardFormValues = {
        interested: jobBoardData?.isInterested ?? null,
        sectorIds: jobBoardData?.sectorPrefs?.map((s) => s.sector?.id) || [],
        locationIds:
            jobBoardData?.officePrefs?.map((o) => o.companyOfficeId) || [],
        interestedComments: jobBoardData?.isInterested
            ? jobBoardData.comments || ''
            : '',
        notInterestedComments:
            jobBoardData?.isInterested === false
                ? jobBoardData.comments || ''
                : '',
        notInterestedReason: jobBoardData?.notInterestedReason || null,
        disabled: false,
    }

    const onSubmit = async () => {
        const sharedVariables = {
            candidateJobBoardId: jobBoardId || '',
            isInterested: formik.values.interested || false,
        }
        const interestedVariables = {
            comments: formik.values.interestedComments,
            companyOfficePrefIds: formik.values.locationIds,
            sectorPrefIds: formik.values.sectorIds,
            ...sharedVariables,
        }
        const notInterestedVariables = {
            comments: formik.values.notInterestedComments,
            notInterestedReason: formik.values.notInterestedReason,
            ...sharedVariables,
        }
        await updateJobBoardResponse({
            refetchQueries: [
                GetFilteredRostersWithCandidate,
                GetRosterSearchV2,
                'GetRosterCandidatePanelData',
            ],
            variables: formik.values.interested
                ? interestedVariables
                : notInterestedVariables,
            onError: (error) => {
                toast({
                    title: 'Error',
                    message: error.message || 'Something went wrong.',
                })
                console.error('Error', error)
            },
            onCompleted: (response) => {
                if (response.updateCandidatesJobBoardResponse.success) {
                    toast({
                        title: 'Success',
                        severity: 'success',
                        message:
                            response.updateCandidatesJobBoardResponse.message ||
                            'Job board response updated successfully.',
                    })
                } else {
                    toast({
                        title: 'Error',
                        message:
                            response.updateCandidatesJobBoardResponse.message ||
                            'Something went wrong.',
                    })
                }
            },
        })
    }

    const { setFieldValue, ...formik } = useFormik<JobBoardFormValues>({
        initialValues,
        validationSchema,
        onSubmit,
    })

    useEffect(() => {
        if (
            jobBoardData?.isInterested !== undefined &&
            jobBoardData.isInterested !== null
        ) {
            setFieldValue('interested', jobBoardData.isInterested)
            setFieldValue(
                'sectorIds',
                jobBoardData.sectorPrefs?.map((s) => s.sector?.id)
            )
            setFieldValue(
                'locationIds',
                jobBoardData.officePrefs?.map((o) => o.companyOfficeId)
            )
            if (jobBoardData.isInterested === true)
                setFieldValue('interestedComments', jobBoardData.comments)
            if (jobBoardData.isInterested === false)
                setFieldValue('notInterestedComments', jobBoardData.comments)
            setFieldValue(
                'notInterestedReason',
                jobBoardData.notInterestedReason
            )
        }
    }, [jobBoardData, setFieldValue])

    return (
        <Stack>
            <Typography variant="h5">Job Board</Typography>
            <UpdatedText
                updatedAt={jobBoardData?.updatedAt || jobBoardData?.createdAt}
                updatedBy={getFullName(
                    jobBoardData?.updatedByUser ||
                        jobBoardData?.createdByUser ||
                        {}
                )}
            />
            <Stack
                alignItems="flexStart"
                justifyContent="center"
                padding={getPxAsRem(8, 4)}
                gap={2}
            >
                <Typography variant="body1">{jobBoardName}</Typography>
                <Typography
                    variant="body2"
                    color={theme.palette.other.secondary_light}
                >
                    {jobBoardDescription}
                </Typography>
                <Stack direction="row" justifyContent="space-between">
                    <Stack>
                        <Typography variant="body2">Company Name</Typography>
                        <Typography
                            color={theme.palette.other.secondary_light}
                            variant="body2"
                        >
                            {companyName}
                        </Typography>
                    </Stack>
                    <Stack>
                        <Typography variant="body2">Start Date</Typography>
                        <Typography
                            color={theme.palette.other.secondary_light}
                            variant="body2"
                        >
                            {startDate}
                        </Typography>
                    </Stack>
                    <Stack>
                        <Typography variant="body2">Titles</Typography>
                        <Typography
                            color={theme.palette.other.secondary_light}
                            variant="body2"
                        >
                            {titles}
                        </Typography>
                    </Stack>

                    <Stack>
                        <Typography variant="body2">Sectors</Typography>
                        <Typography
                            color={theme.palette.other.secondary_light}
                            variant="body2"
                        >
                            {sectors}
                        </Typography>
                    </Stack>
                    <Stack>
                        <Typography variant="body2">Locations</Typography>
                        <Typography
                            color={theme.palette.other.secondary_light}
                            variant="body2"
                        >
                            {locations}
                        </Typography>
                    </Stack>
                </Stack>
                <form>
                    <RadioGroup
                        value={formik.values.interested}
                        row
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-evenly',
                            flexGrow: 1,
                            margin: 3,
                            marginTop: 1,
                        }}
                    >
                        <FormControlLabel
                            disabled={formik.values.disabled || !canEdit}
                            control={
                                <Radio
                                    value={true}
                                    onClick={() =>
                                        setFieldValue('interested', true)
                                    }
                                />
                            }
                            label="Interested"
                            slotProps={{
                                typography: {
                                    sx: {
                                        fontSize: getPxAsRem(20),
                                        fontFamily: 'Roboto Serif',
                                        fontWeight: 500,
                                    },
                                },
                            }}
                        />
                        <FormControlLabel
                            disabled={formik.values.disabled || !canEdit}
                            control={
                                <Radio
                                    value={false}
                                    onClick={() =>
                                        setFieldValue('interested', false)
                                    }
                                />
                            }
                            label="Not Interested"
                            slotProps={{
                                typography: {
                                    sx: {
                                        fontSize: getPxAsRem(20),
                                        fontFamily: 'Roboto Serif',
                                        fontWeight: 500,
                                    },
                                },
                            }}
                        />
                    </RadioGroup>
                    {formik.values.interested && (
                        <InterestedFormFields
                            canEdit={canEdit ?? false}
                            formik={{ ...formik, setFieldValue }}
                            jobBoard={jobBoardData?.jobBoard}
                        />
                    )}
                    {formik.values.interested === false && (
                        <NotInterestedFormFields
                            canEdit={canEdit ?? false}
                            formik={{ ...formik, setFieldValue }}
                        />
                    )}
                </form>
                <FormHelperText>
                    Changes to these fields will also be reflected in the
                    candidate portal
                </FormHelperText>
                {formik.values.interested !== undefined &&
                    !formik.values.disabled && (
                        <Button
                            onClick={() => formik.handleSubmit()}
                            disabled={!canEdit}
                        >
                            Submit
                        </Button>
                    )}
            </Stack>
        </Stack>
    )
}

export default RosterPanelJobBoard
